// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
/**
 * Utility functions for channel tagging rules field filtering
 */

import type { AvailableFilterField } from "@/hooks/use-available-filter-fields";
import type { FilterGroup, StructuredFilter } from "@/lib/filters";
import { isFilterGroup } from "@/lib/filters";
import type { AvailableFilterFieldResponse } from "@/types/channel-tagging-rules";

/**
 * Record type values from RecordType enum
 */
export const RECORD_TYPES = {
  WEB_PAGE_VIEW: "web_page_view",
  UNMAPPED_CAMPAIGN_MEMBER: "unmapped_campaign_member",
  BULK_EMAIL_EVENT: "bulk_email_event",
  MEETING: "meeting",
  PHONE_CALL: "phone_call",
  DIRECT_EMAIL: "direct_email",
} as const;

/**
 * Record types that have campaign IDs and support campaign fields.
 * Only unmapped_campaign_member and bulk_email_event have associated campaign IDs.
 * Other record types (meeting, phone_call, direct_email) do not have campaign associations.
 */
const CAMPAIGN_BASED_RECORD_TYPES: Set<string> = new Set([
  RECORD_TYPES.UNMAPPED_CAMPAIGN_MEMBER,
  RECORD_TYPES.BULK_EMAIL_EVENT,
]);

/**
 * Special marker for fields that belong to campaign-based record types
 * Used in filtering logic to determine field visibility
 */
const CAMPAIGN_BASED_MARKER = "__CAMPAIGN_BASED__";

/**
 * Field mappings to record types
 * Fields that are specific to certain record types
 */
const FIELD_RECORD_TYPE_MAP: Record<string, string> = {
  // Web page view fields
  utm_source: RECORD_TYPES.WEB_PAGE_VIEW,
  utm_medium: RECORD_TYPES.WEB_PAGE_VIEW,
  utm_campaign: RECORD_TYPES.WEB_PAGE_VIEW,
  utm_content: RECORD_TYPES.WEB_PAGE_VIEW,
  utm_term: RECORD_TYPES.WEB_PAGE_VIEW,
  referrer_url: RECORD_TYPES.WEB_PAGE_VIEW,
  url: RECORD_TYPES.WEB_PAGE_VIEW,
  page_type: RECORD_TYPES.WEB_PAGE_VIEW,
  web_session_channel: RECORD_TYPES.WEB_PAGE_VIEW,

  // Campaign fields - available for all campaign-based record types
  campaign_name: CAMPAIGN_BASED_MARKER,
  campaign_type: CAMPAIGN_BASED_MARKER,
  campaign_start_date: CAMPAIGN_BASED_MARKER,
  campaign_end_date: CAMPAIGN_BASED_MARKER,
};

/**
 * Enriches field definitions with record_type information
 */
export function enrichFieldDefinitionsWithRecordType(
  fields: AvailableFilterFieldResponse[],
): AvailableFilterFieldResponse[] {
  return fields.map((field) => ({
    ...field,
    record_type: FIELD_RECORD_TYPE_MAP[field.id] || null,
  }));
}

/**
 * Extracts record_type filter values from a FilterGroup.
 * Returns an array of record_type values found in the conditions.
 * Returns empty array if no record_type filters are found.
 *
 * @param conditions The filter conditions to search
 * @param shallow If true, only look at immediate filters (not nested groups)
 */
export function extractRecordTypeFilters(
  conditions: FilterGroup | StructuredFilter | null | undefined,
  shallow = false,
): string[] {
  if (!conditions) return [];

  const recordTypes = new Set<string>();

  function traverse(node: FilterGroup | StructuredFilter, depth: number) {
    if (isFilterGroup(node)) {
      // If shallow mode, only process immediate filters (depth 0)
      if (shallow && depth > 0) return;
      node.filters.forEach((filter) => traverse(filter, depth + 1));
    } else {
      // Check if this is a record_type filter
      if (node.field === "record_type" && node.values) {
        const values = Array.isArray(node.values) ? node.values : [node.values];
        values.forEach((value) => {
          if (typeof value === "string") {
            recordTypes.add(value);
          }
        });
      }
    }
  }

  traverse(conditions, 0);
  return Array.from(recordTypes);
}

/**
 * Core field filtering logic based on record types.
 * Returns filtered fields based on the provided record types.
 */
function filterFieldsByRecordTypes(
  fields: AvailableFilterField[],
  recordTypes: string[],
): AvailableFilterField[] {
  // If no record_type filters, show all fields
  if (recordTypes.length === 0) {
    return fields;
  }

  // Check if ANY selected record type matches each category
  const hasAnyCampaignBased = recordTypes.some((rt) =>
    CAMPAIGN_BASED_RECORD_TYPES.has(rt),
  );
  const hasAnyWebPageView = recordTypes.some(
    (rt) => rt === RECORD_TYPES.WEB_PAGE_VIEW,
  );

  return fields.filter((field) => {
    // Always show the record_type field itself
    if (field.id === "record_type") {
      return true;
    }

    // Check if field has record_type metadata
    const fieldRecordType = FIELD_RECORD_TYPE_MAP[field.id];

    // Show field if it has no specific record_type (common field)
    if (!fieldRecordType) {
      return true;
    }

    // Show campaign fields if ANY selected type supports campaign associations
    if (hasAnyCampaignBased && fieldRecordType === CAMPAIGN_BASED_MARKER) {
      return true;
    }

    // Show web page view fields if ANY selected type is web_page_view
    if (hasAnyWebPageView && fieldRecordType === RECORD_TYPES.WEB_PAGE_VIEW) {
      return true;
    }

    return false;
  });
}

/**
 * Filters available fields based on record_type conditions (searches entire tree).
 *
 * Rules:
 * - If no record_type filters are detected, show all fields
 * - If ANY selected record_type is campaign-based, show campaign fields + common fields
 * - If ANY selected record_type is web_page_view, show web fields + common fields
 * - If mixed, both campaign and web fields are shown
 *
 * @param fields All available field definitions
 * @param conditions The current filter conditions
 * @returns Filtered field definitions
 */
export function filterFieldsByRecordType(
  fields: AvailableFilterField[],
  conditions: FilterGroup,
): AvailableFilterField[] {
  const recordTypes = extractRecordTypeFilters(conditions);
  return filterFieldsByRecordTypes(fields, recordTypes);
}

/**
 * Creates a function that filters fields for a specific filter group.
 * This is used for per-group field filtering where each nested group
 * only shows fields relevant to its own record_type filters.
 *
 * @param allFields All available field definitions
 * @returns A function that takes a FilterGroup and returns filtered fields
 */
export function createGroupFieldFilter(
  allFields: AvailableFilterField[],
): (group: FilterGroup) => AvailableFilterField[] {
  return (group: FilterGroup) => {
    // Extract record_types only from immediate filters in this group (shallow)
    const recordTypes = extractRecordTypeFilters(group, true);
    return filterFieldsByRecordTypes(allFields, recordTypes);
  };
}
